import { observable, action, decorate } from 'mobx'
import moment from 'moment'

class FAA {
  fieldNames = [
    'userid',
    'medicalfacilityid',
    'physicianid',
    'approvalPracticeCom',
    'approvalPatientCom',
    'firstName',
    'middleName',
    'lastName',
    'sex',
    'state',
    'streetAddress',
    'city',
    'zipCode',
    'acknowledge',
    'phoneNumber',
    'email',
    'facility',
    'facilityStreetAddress',
    'facilityCity',
    'facilityStateOrProvince',
    'facilityPostalCode',
    'orderingPhysician',
    'physicianPhone',
    'physicianFax',
    'physicianEmail',
    'totalGross',
    'familyMembers',
    'circumstance',
    'otherCircumstances',
    'patientDateOfBirth',
    'approvalDecision',
    'approvalPracticeComEmail',
    'approvalPracticeComMail',
    'approvalPracticeComFax',
    'who',
    'whoFirst',
    'whoLast',
    'whoRelation',
    'signatureText',
    'signatureDate',
    'phoneAuthorization'
  ]

  REQUIRED_FIELDS = [
    'firstName',
    'lastName',
    'sex',
    'streetAddress',
    'city',
    'state',
    'zipCode',
    'totalGross',
    'familyMembers',
    'circumstance',
    'acknowledge',
    'signatureText',
    'signatureDate',
    'patientDateOfBirth',
    'who'
  ]

  // @Observables
  orderSuccess = false
  modalDisplay = false
  approvalPracticeCom = { value: '', error: false }
  approvalPatientCom = { value: '', error: false }
  firstName = { value: '', error: false }
  middleName = { value: '', error: false }
  lastName = { value: '', error: false }
  sex = { value: '', error: false }
  state = { value: '', error: false }
  streetAddress = { value: '', error: false }
  city = { value: '', error: false }
  zipCode = { value: '', error: false }
  acknowledge = { value: '', error: false }
  phoneNumber = { value: '', error: false }
  email = { value: '', error: false }
  facility = { value: '', error: false }
  facilityStreetAddress = { value: '', error: false }
  facilityCity = { value: '', error: false }
  facilityStateOrProvince = { value: '', error: false }
  facilityPostalCode = { value: '', error: false }
  orderingPhysician = { value: '', error: false }
  physicianPhone = { value: '', error: false }
  physicianFax = { value: '', error: false }
  physicianEmail = { value: '', error: false }
  totalGross = { value: '', error: false }
  familyMembers = { value: '', error: false }
  circumstance = { value: '', error: false }
  otherCircumstances = { value: '', error: false }
  patientDateOfBirth = { value: '', error: false }
  approvalDecision = { patient: false, practice: false, error: false }
  approvalPracticeComEmail = { value: '', error: false }
  approvalPracticeComMail = { value: '', error: false }
  approvalPracticeComFax = { value: '', error: false }
  who = { value: '', error: false }
  whoFirst = { value: '', error: false }
  whoLast = { value: '', error: false }
  whoRelation = { value: '', error: false }
  phoneAuthorization = { value: false, error: false }
  signatureText = { value: '', error: false }
  signatureDate = { value: '', error: false }
  showPatient = false
  showPractice = false
  valid = true
  confirmationNumber = ''
  otherCircumstancesSelected = false
  temporaryLossSelected = false
  permanentLossSelected = false
  receiveCharityCareSelected = false
  formHasError = false
  sendingForm = false
  physicianid = ''
  medicalfacilityid = ''
  userid = ''
  preliminaryAwardApproval = ''
  attachments = []

  setSignatureText (signatureText) {
    this.signatureText = signatureText
  }

  setOrderSuccess (success) {
    this.orderSuccess = success
  }

  setSignatureDate (signatureDate) {
    this.signatureDate = signatureDate
  }

  updateFieldValue (name, value) {
    this[name].value = value
  }

  updateFieldError (name, value = true) {
    this[name].error = value
  }

  setField (name, value) {
    this[name] = value
  }

  fieldExists (name) {
    return !!this[name]
  }

  cleanData () {
    this.fieldNames.forEach(field => {
      this[field] = { value: '', error: false }
    })
    this.approvalDecision = {
      patient: false,
      practice: false,
      error: false
    }
    this.phoneAuthorization.value = false
    this.modalDisplay = false
    this.showPatient = false
    this.showPractice = false
    this.valid = true
    this.confirmationNumber = ''
    this.physicianid = ''
    this.medicalfacilityid = ''
    this.userid = ''
    this.preliminaryAwardApproval = ''
  }

  getDataValues () {
    return this.fieldNames.map(name => {
      let value = this[name].value

      // FAA/ONO related fields
      const userRelatedFields = [
        'physicianid',
        'medicalfacilityid',
        'userid'
      ]

      if (userRelatedFields.includes(name)) {
        value = this[name]
      }

      if (name === 'approvalDecision') {
        const { patient, practice } = this[name]
        value = []
        if (patient === true) value.push('patient')
        if (practice === true) value.push('practice')
      }

      if ((name === 'signatureDate' || name === 'patientDateOfBirth') && value !== '') {
        value = moment(value).format('YYYY-MM-DD')
      }

      if (name === 'sex' || name === 'state' || name === 'facilityStateOrProvince') value = value.value

      if (name === 'acknowledge') value = value.toString()

      return {
        name,
        value
      }
    })
  }

  getValue (name) {
    return this[name].value
  }

  getProperty (name) {
    return this[name]
  }

  hasError (name) {
    return this[name].error
  }

  setFormIsValid (valid) {
    this.valid = valid
  }

  isFormValid () {
    return this.valid
  }

  setConfirmationNumber (confirmationNumber) {
    this.confirmationNumber = confirmationNumber
  }

  setPreliminaryAwardApproval (preliminaryAwardApproval) {
    this.preliminaryAwardApproval = preliminaryAwardApproval
  }

  setFormError (hasError = false) {
    this.formHasError = hasError
  }

  setErrorWhenSendDocuments (error) {
    this.errorSendingDocuments = error
  }

  setFormSubmissionModalDisplay () {
    this.modalDisplay = !this.modalDisplay
  }

  setSendingForm (sending = true) {
    this.sendingForm = sending
  }

  setApprovalDecisionPractice (checked) {
    this.approvalDecision.practice = checked
    if (checked) this.approvalDecision.error = false
  }

  setApprovalDecisionPatient (checked) {
    this.approvalDecision.patient = checked
    if (checked) this.approvalDecision.error = false
  }

    
  addAttachments = (files) => {
    this.attachments = [
      ...this.attachments,
      ...files
    ]
  }

  resetAttachements = () => {
    this.attachments = []
  }

  removeAttachment = (index) => {
    this.attachments = [
      ...this.attachments.slice(0, index),
      ...this.attachments.slice(index + 1)
    ]
  }
}

decorate(FAA, {
  approvalPracticeCom: observable,
  approvalPatientCom: observable,
  firstName: observable,
  middleName: observable,
  lastName: observable,
  sex: observable,
  state: observable,
  streetAddress: observable,
  city: observable,
  zipCode: observable,
  acknowledge: observable,
  phoneNumber: observable,
  email: observable,
  facility: observable,
  facilityStreetAddress: observable,
  facilityCity: observable,
  facilityStateOrProvince: observable,
  facilityPostalCode: observable,
  orderingPhysician: observable,
  physicianPhone: observable,
  physicianFax: observable,
  physicianEmail: observable,
  totalGross: observable,
  familyMembers: observable,
  circumstance: observable,
  otherCircumstances: observable,
  patientDateOfBirth: observable,
  approvalDecision: observable,
  approvalPracticeComEmail: observable,
  approvalPracticeComMail: observable,
  approvalPracticeComFax: observable,
  who: observable,
  whoFirst: observable,
  whoLast: observable,
  whoRelation: observable,
  phoneAuthorization: observable,
  signatureText: observable,
  signatureDate: observable,
  orderSuccess: observable,
  modalDisplay: observable,
  showPatient: observable,
  showPractice: observable,
  valid: observable,
  confirmationNumber: observable,
  preliminaryAwardApproval: observable,
  otherCircumstancesSelected: observable,
  temporaryLossSelected: observable,
  permanentLossSelected: observable,
  receiveCharityCareSelected: observable,
  formHasError: observable,
  sendingForm: observable,
  physicianid: observable,
  medicalfacilityid: observable,
  userid: observable,
  attachments: observable,
  setSignatureText: action,
  clearSignatureText: action,
  setSignatureDate: action,
  clearSignatureDate: action,
  updateFieldValue: action,
  updateFieldError: action,
  setField: action,
  setFormIsValid: action,
  setConfirmationNumber: action,
  setPreliminaryAwardApproval: action,
  setFormSubmissionModalDisplay: action,
  setFormError: action,
  fieldExists: action,
  cleanData: action,
  getDataValues: action,
  setSendingForm: action,
  setApprovalDecisionPractice: action,
  setApprovalDecisionPatient: action,
  addAttachments: action,
  removeAttachment: action,
  resetAttachements: action
})

export default new FAA()
